import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { ChevronsUpDown } from "lucide-react";
import { fromJS } from "immutable";
import InputFieldDebounce from "../../components/ui/InputFieldDebounce";
import { SelectField } from "../../components/ui";
import LabelsSelect from "../../components/LabelsSelect";
import CustomField from "../CustomField";
import EmailInputFieldDebounce from "../../components/ui/EmailInputFieldDebounce";
import PhoneInputFieldDebounce from "../../components/ui/PhoneInputFieldDebounce";
import AddressGroupSelect from "../../components/AddressGroupSelect";
import DomainsSelect from "../../components/DomainsSelect";
import BucketSelect from "../../components/BucketSelect";
import AssigneesSelect from "../../components/AssigneesSelect";
import ItemSelect from "../../components/ItemSelect";
import NumericFormatFieldDebounce from "../../components/ui/NumericFormatFieldDebounce";

const CompanyForm = ({
  projectId,
  visibleFieldIds,
  isEditMode: _isEditMode,
  taskData,
  onDataChange,
  validationErrors,
}) => {
  const { t } = useTranslation();

  const projectCurrency = useSelector((state: any) =>
    state.projectReducer.getIn(["project", "currency"])
  );
  const currency = projectCurrency || "USD";
  const fields =
    useSelector((state: any) => state.projectReducer.getIn(["project", "fields"])) || fromJS([]);
  const industries = useSelector((state: any) => state.ruleReducer.getIn(["industry"])).toJS();

  const customFields = fields.filter(
    (field: any) => field.get("isCustomField") && field.get("formType") == "COMPANY"
  );

  const getTaskFieldValue = (fieldId: string) => {
    return taskData.getIn([fieldId, "value"]);
  };

  const handleChangeField = (fieldId, fieldValue) => {
    onDataChange(fieldId, fieldValue);
  };

  const BUILD_FIELDS: Record<string, any> = {
    name: (
      <div>
        <InputFieldDebounce
          required
          testId="account-form-name-input"
          name="name"
          label={t("Account Name")}
          placeholder={t("Set account name...")}
          value={getTaskFieldValue("name")}
          onChange={(value) => handleChangeField("name", value)}
          errorMessage={validationErrors.name}
        />
      </div>
    ),
    description: (
      <div>
        <InputFieldDebounce
          mode="textarea"
          testId="account-form-description-input"
          label={t("Description")}
          placeholder={t("Set description...")}
          value={getTaskFieldValue("description")}
          onChange={(value) => handleChangeField("description", value)}
          errorMessage={validationErrors.description}
        />
      </div>
    ),
    domains: (
      <div className="w-full">
        <label className="block text-sm font-semibold text-gray-800 mb-2">{t("Domains")}</label>
        <DomainsSelect
          testId="account-form-domains-input"
          type="FORM"
          value={getTaskFieldValue("domains")?.toJS() || []}
          onChange={(domains: string[]) => handleChangeField("domains", domains)}
          placeholder={t("Set domains (e.g., example.com)")}
          showRemove
        />
      </div>
    ),
    email: (
      <div>
        <EmailInputFieldDebounce
          testId="account-form-email-input"
          label={t("Email")}
          placeholder={t("Set email...")}
          value={getTaskFieldValue("email")}
          onChange={(value) => handleChangeField("email", value)}
          errorMessage={validationErrors.email}
        />
      </div>
    ),
    phone: (
      <div>
        <PhoneInputFieldDebounce
          testId="account-form-phone-input"
          label={t("Phone number")}
          placeholder={t("Set phone number...")}
          value={getTaskFieldValue("phone")}
          onChange={(value) => handleChangeField("phone", value)}
          errorMessage={validationErrors.phone}
        />
      </div>
    ),
    industry: (
      <div className="w-full">
        <label className="block text-sm font-semibold text-gray-800 mb-2">{t("Industry")}</label>
        <SelectField
          testId="account-form-industry-input"
          menuPlacement="auto"
          options={industries}
          value={industries.find((item) => item.value === getTaskFieldValue("industry"))}
          onChange={(selectedOption) => {
            handleChangeField("industry", selectedOption?.value);
          }}
          placeholder={t("Select...")}
          isClearable
          dropdownIndicator={<ChevronsUpDown className="w-3.5 h-3.5 text-gray-400" />}
        />
      </div>
    ),
    contact: (
      <div className="w-full">
        <label className="block text-sm font-semibold text-gray-800 mb-2">
          {t("Primary contact")}
        </label>
        <ItemSelect
          testId="account-form-contact-input"
          projectId={projectId}
          contentType="CONTACT"
          type="FORM"
          value={getTaskFieldValue("contact")?.toJS()}
          onChange={(contact: any) => handleChangeField("contact", contact)}
          placeholder={t("Select contact")}
          isClearable
        />
      </div>
    ),
    assignees: (
      <div className="w-full">
        <label className="block text-sm font-semibold text-gray-800 mb-2">{t("Owners")}</label>
        <AssigneesSelect
          type="FORM"
          value={getTaskFieldValue("assignees")?.toJS() || []}
          onChange={(assigneeIds: string[]) => handleChangeField("assignees", assigneeIds)}
          placeholder={t("Select owners")}
        />
      </div>
    ),
    labels: (
      <div className="w-full">
        <label className="block text-sm font-semibold text-gray-800 mb-2">{t("Labels")}</label>
        <LabelsSelect
          projectId={projectId}
          contentType="COMPANY"
          type="FORM"
          value={getTaskFieldValue("labels")?.toJS() || []}
          onChange={(labelIds: string[]) => handleChangeField("labels", labelIds)}
          placeholder={t("Select labels")}
          showRemove
        />
      </div>
    ),
    bucketId: (
      <div className="w-full">
        <label className="block text-sm font-semibold text-gray-800 mb-2">{t("Group")}</label>
        <BucketSelect
          projectId={projectId}
          contentType="COMPANY"
          type="FORM"
          value={getTaskFieldValue("bucketId")}
          onChange={(bucketId: string) => handleChangeField("bucketId", bucketId)}
          placeholder={t("Select group")}
          isClearable
        />
        {validationErrors.bucketId && (
          <p className="text-xs text-red-600 mt-1">{validationErrors.bucketId}</p>
        )}
      </div>
    ),
    // All address
    addressGroup: (
      <div>
        <label className="block text-sm font-semibold text-gray-800 mb-2">{t("Address")}</label>
        <AddressGroupSelect
          testIdPrefix="account-form-address"
          type="FORM"
          street={getTaskFieldValue("street")}
          onStreetChange={(street) => handleChangeField("street", street)}
          city={getTaskFieldValue("city") ? getTaskFieldValue("city").toJS() : undefined}
          onCityChange={(city) => handleChangeField("city", city)}
          state={getTaskFieldValue("state") ? getTaskFieldValue("state").toJS() : undefined}
          onStateChange={(state) => handleChangeField("state", state)}
          country={getTaskFieldValue("country") ? getTaskFieldValue("country").toJS() : undefined}
          onCountryChange={(country) => handleChangeField("country", country)}
          placeholder={t("Set a value...")}
        />
      </div>
    ),
    website: (
      <div>
        <InputFieldDebounce
          testId="account-form-website-input"
          label={t("Website")}
          placeholder={t("Set a website")}
          value={getTaskFieldValue("website")}
          onChange={(value) => handleChangeField("website", value)}
          errorMessage={validationErrors.website}
        />
      </div>
    ),
    employeeRange: (
      <div>
        <InputFieldDebounce
          testId="account-form-employee-range-input"
          label={t("Employee range")}
          placeholder={t("Set a value")}
          value={getTaskFieldValue("employeeRange")}
          onChange={(value) => handleChangeField("employeeRange", value)}
          errorMessage={validationErrors.employeeRange}
        />
      </div>
    ),
    estimatedArr: (
      <div>
        <NumericFormatFieldDebounce
          testId="account-form-estimated-arr-input"
          label={t("Estimated Arr")}
          value={getTaskFieldValue("estimatedArr")}
          onChange={(value: string) => {
            handleChangeField("estimatedArr", value);
          }}
          placeholder={t("Set a value")}
          errorMessage={validationErrors.estimatedArr}
          currency={currency}
        />
      </div>
    ),
    linkedIn: (
      <div>
        <InputFieldDebounce
          testId="account-form-linkedin-input"
          label={t("LinkedIn")}
          placeholder={t("Set a link (e.g., linkedin.com/in/username)")}
          value={getTaskFieldValue("linkedIn")}
          onChange={(value) => handleChangeField("linkedIn", value)}
          errorMessage={validationErrors.linkedIn}
        />
      </div>
    ),
    instagram: (
      <div>
        <InputFieldDebounce
          testId="account-form-instagram-input"
          label={t("Instagram")}
          placeholder={t("Set a link (e.g., instagram.com/username)")}
          value={getTaskFieldValue("instagram")}
          onChange={(value) => handleChangeField("instagram", value)}
          errorMessage={validationErrors.instagram}
        />
      </div>
    ),
    twitter: (
      <div>
        <InputFieldDebounce
          testId="account-form-twitter-input"
          label={t("Twitter")}
          placeholder={t("Set a link (e.g., x.com/username)")}
          value={getTaskFieldValue("twitter")}
          onChange={(value) => handleChangeField("twitter", value)}
          errorMessage={validationErrors.twitter}
        />
      </div>
    ),
    facebook: (
      <div>
        <InputFieldDebounce
          testId="account-form-facebook-input"
          label={t("Facebook")}
          placeholder={t("Set a link (e.g., facebook.com/username)")}
          value={getTaskFieldValue("facebook")}
          onChange={(value) => handleChangeField("facebook", value)}
          errorMessage={validationErrors.facebook}
        />
      </div>
    ),
  };

  return (
    <div className="space-y-4">
      {visibleFieldIds.map((visibleFieldId) => {
        const customField = customFields.find((field) => field.get("id") === visibleFieldId);
        if (customField && customField.get("id")) {
          const fieldData = {
            ...customField.toJS(),
            value: getTaskFieldValue(customField.get("id")),
          };
          return (
            <div className="w-full" key={`${customField.get("id")}`}>
              <label className="block text-sm font-semibold text-gray-800 mb-2">
                {t(customField.get("label"))}
              </label>
              <CustomField
                field={fieldData}
                onChange={(value: any) => {
                  handleChangeField(customField.get("id"), value);
                }}
              />
            </div>
          );
        }
        return <React.Fragment key={visibleFieldId}>{BUILD_FIELDS[visibleFieldId]}</React.Fragment>;
      })}
    </div>
  );
};

export default CompanyForm;
